import { useEffect } from 'react';

const FOCUSABLE =
  'input, textarea, select, button, [contenteditable="true"], [tabindex]';

/** How long after a press a jump to the panel still counts as a steal. */
const STEAL_WINDOW_MS = 600;
/** Belt and braces: never trade focus back and forth indefinitely. */
const MAX_RESTORES = 5;

/**
 * Gives back the focus kula's Drawer takes off you.
 *
 * The Drawer is a Radix Dialog, whose FocusScope keeps focus trapped by watching
 * the panel for removed nodes:
 *
 *   if (mutation.removedNodes.length > 0) {
 *     if (!container.contains(document.activeElement)) focus(container);
 *   }
 *
 * react-select removes its `aria-selection` / `aria-context` live regions when
 * its input blurs, and those live inside the panel. Clicking straight from the
 * recipients field to another control therefore removes nodes in the same tick
 * as the focus hand-off — and in that window `document.activeElement` is briefly
 * `body`, so FocusScope decides focus escaped and pulls it onto the panel. The
 * press is lost and the field needs a second click.
 *
 * Verified in the browser: `focusout` reports `relatedTarget` as the intended
 * field (so FocusScope's other branch is not involved), and the very next
 * `focusin` is the panel itself.
 *
 * So: remember what was pressed, and if focus lands on the panel rather than
 * that element, hand it back. react-select removes its two live regions in
 * separate mutation batches, so FocusScope steals more than once per click —
 * the candidate is therefore kept for a short window after the press rather
 * than consumed on the first restore. The window and the attempt cap keep this
 * from becoming a tug-of-war with anything that legitimately wants the panel.
 */
export const useRestoreStolenFocus = (active: boolean) => {
  useEffect(() => {
    if (!active) return;

    let pressed: HTMLElement | null = null;
    let pressedAt = 0;
    let restores = 0;

    const onPointerDown = (event: Event) => {
      const target = event.target as HTMLElement | null;
      pressed = target?.closest?.(FOCUSABLE) ?? null;
      pressedAt = Date.now();
      restores = 0;
    };

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!pressed || !target || target.getAttribute('role') !== 'dialog')
        return;
      if (target === pressed || !target.contains(pressed)) return;
      // react-select removes its two live regions in separate mutation batches,
      // so FocusScope steals more than once per click — keep the candidate for
      // the whole burst rather than spending it on the first restore.
      if (
        Date.now() - pressedAt > STEAL_WINDOW_MS ||
        restores >= MAX_RESTORES
      ) {
        pressed = null;
        return;
      }

      restores += 1;
      pressed.focus();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('focusin', onFocusIn, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('focusin', onFocusIn, true);
    };
  }, [active]);
};
